import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import {
  IValueChangedEvent,
  IComponentStateHistory,
  IWizardContentComponent
} from '../../../wizard.content';
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  AbstractControl,
  ValidationErrors,
  UntypedFormControl
} from '@angular/forms';
import { isEmpty, toString } from 'lodash';
import { REMOVED } from '../../../services/base.wizard.step';
import { ValuesExchangeProvider, TimeoutWrapperService } from '@nrwl/common-lib';
import { Subscription } from 'rxjs';

const NAME_FROM = 'from';
const NAME_TO = 'to';

export enum NAMES {
  FROM = 'Warning',
  TO = 'Critical'
}

@Component({
  selector: 'nrwl-threshold-historical-settings-page',
  templateUrl: './threshold.historical.settings.page.component.html',
  styleUrls: ['./threshold.historical.settings.page.component.scss']
})
export class ThresholdHistoricalSettingsPageComponent
  implements IWizardContentComponent, OnInit, OnDestroy {
  @Input() step: any = {}; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() subStep: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() wizardCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() previousOutput: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @Input() stateHistory?: IComponentStateHistory;

  historicalThresholdsForm: UntypedFormGroup;
  formChanges: Subscription;
  fromLableName: string;
  toLableName: string;
  loadedThresholds: any = ''; //eslint-disable-line @typescript-eslint/no-explicit-any
  isLoading = false;
  thresholds: any = {}; //eslint-disable-line @typescript-eslint/no-explicit-any

  constructor(
    private fb: UntypedFormBuilder,
    private readonly exchange: ValuesExchangeProvider,
    private readonly timeoutWrapper: TimeoutWrapperService
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  get fromCtrl(): any {
    return this.historicalThresholdsForm.get(NAME_FROM);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  get toCtrl(): any {
    return this.historicalThresholdsForm.get(NAME_TO);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  showErrorMsg(): any {
    return this.historicalThresholdsForm.hasError('thresholdError');
  }

  ngOnInit() {
    this.loadThresholds();
    this.historicalThresholdsForm = this.fb.group({}, { validators: this.validateThreshold });
    this.historicalThresholdsForm.addControl(
      NAME_FROM,
      new UntypedFormControl('', [
        Validators.required,
        Validators.min(this.thresholds.minValue),
        Validators.max(this.thresholds.maxValue)
      ])
    );
    this.historicalThresholdsForm.addControl(
      NAME_TO,
      new UntypedFormControl('', [
        Validators.required,
        Validators.min(this.thresholds.minValue),
        Validators.max(this.thresholds.maxValue)
      ])
    );
    this.step.form = this.historicalThresholdsForm;
    this.resetOutput();
    this.registerFormChangesListeners();
    this.wizardCtrl.toggleFinish = this.save.bind(this);
  }

  ngOnDestroy() {
    if (this.formChanges) {
      this.formChanges.unsubscribe();
    }
  }

  registerFormChangesListeners(): void {
    this.formChanges = this.historicalThresholdsForm.valueChanges.subscribe(newValue => {
      const updatedFields = Object.keys(newValue);
      if (updatedFields.includes(NAME_FROM)) {
        this.updateFromValueThreshold(newValue[NAME_FROM]);
      }
      if (updatedFields.includes(NAME_TO)) {
        this.updateToValueThreshold(newValue[NAME_TO]);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  getRequiredMessage(lableName): any {
    return lableName.toLowerCase() + ' range threshold is required';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  getMinMaxMessage(lableName): any {
    return (
      lableName.toLowerCase() +
      ' range should be between ' +
      this.thresholds.minValue +
      ' and ' +
      this.thresholds.maxValue
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  getFromToValidMessage(): any {
    return (
      this.toLableName.toLowerCase() +
      ' value should be greater than ' +
      this.fromLableName.toLocaleLowerCase()
    );
  }

  resetOutput(): void {
    const isReversed = this.thresholds.reversed;
    if (!isReversed) {
      this.fromLableName = NAMES.FROM;
      this.toLableName = NAMES.TO;
    } else {
      this.fromLableName = NAMES.TO;
      this.toLableName = NAMES.FROM;
    }

    let fromValue = this.thresholds.valueFrom;
    let toValue = this.thresholds.valueTo;
    if (!this.wizardCtrl.wizardService.editMode) {
      const fromValueId = this.stateHistory(this.componentId, NAME_FROM + 'Id');
      if (fromValueId && fromValueId.value && fromValueId.value !== REMOVED) {
        fromValue = fromValueId.value;
      }
      const toValueId = this.stateHistory(this.componentId, NAME_TO + 'Id');
      if (toValueId && toValueId.value && toValueId.value !== REMOVED) {
        toValue = toValueId.value;
      }
    }
    this.historicalThresholdsForm.get(NAME_FROM).setValue(fromValue);
    this.historicalThresholdsForm.get(NAME_TO).setValue(toValue);
    this.timeoutWrapper.set(
      'historicalSettings',
      () => {
        this.updateFromValueThreshold(fromValue);
        this.updateToValueThreshold(toValue);
      },
      0
    );
  }

  validateThreshold(control: AbstractControl): ValidationErrors | null {
    if (control && control.get(NAME_FROM) && control.get(NAME_TO)) {
      const fromValue = control.get(NAME_FROM).value;
      const toValue = control.get(NAME_TO).value;
      return fromValue >= toValue ? { thresholdError: true } : null;
    }
    return null;
  }

  loadThresholds(): void {
    this.thresholds = { ...this.step.parent.defaultParamsById };
    this.loadedThresholds = JSON.stringify(this.thresholds);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  hasChanged(): any {
    return !isEmpty(this.thresholds) && JSON.stringify(this.thresholds) !== this.loadedThresholds;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  updateFromValueThreshold(value: any): void {
    this.thresholds.valueFrom = value;
    const isValid = !this.showErrorMsg() && this.hasChanged();
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: NAME_FROM + 'Id',
      key: 'valueFrom',
      newValue: value,
      isValid: isValid
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  updateToValueThreshold(value: any): void {
    this.thresholds.valueTo = value;
    const isValid = !this.showErrorMsg() && this.hasChanged();
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: NAME_TO + 'Id',
      key: 'valueTo',
      newValue: value,
      isValid: isValid
    });
  }

  save(): void {
    this.wizardCtrl.getWizardService().dirtyFlag = false;
    if (this.thresholds.mode === 'add') {
      switch (this.thresholds.dimensionValue) {
        case 'site': {
          const siteId = this.step.parent.output['siteId'];
          this.thresholds['staticValues'] = toString(siteId);
          break;
        }
        case 'app': {
          const appId = this.step.parent.output['appId'];
          this.thresholds['staticValues'] = toString(appId);
          break;
        }
        case 'appSite': {
          const siteId = this.step.parent.output['siteId'];
          const appId = this.step.parent.output['appId'];
          this.thresholds['staticValues'] = appId + ',' + siteId;
          break;
        }
        case 'server': {
          const serverIp = this.step.parent.output['serverIp'];
          this.thresholds['staticValues'] = toString(serverIp);
          break;
        }
        case 'appServer': {
          const serverIp = this.step.parent.output['serverIp'];
          const appId = this.step.parent.output['appId'];
          this.thresholds['staticValues'] = appId + ',' + serverIp;
          break;
        }
        case 'siteServer': {
          const serverIp = this.step.parent.output['serverIp'];
          const siteId = this.step.parent.output['siteId'];
          this.thresholds['staticValues'] = siteId + ',' + serverIp;
          break;
        }
        case 'appSiteServer': {
          const siteId = this.step.parent.output['siteId'];
          const appId = this.step.parent.output['appId'];
          const serverIp = this.step.parent.output['serverIp'];
          this.thresholds['staticValues'] = appId + ',' + siteId + ',' + serverIp;
        }
      }
    }
    this.exchange.notifyListeners('update_threshold', this.thresholds);
    this.wizardCtrl.toggleClose();
  }
}
